import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { astraAPI } from '../../../services/astraApi';
import { SkeletonTable } from '../../../components/ui/Skeleton';

/**
 * Current Holdings Component
 *
 * 🔧 PRODUCTION REDESIGN (2025-12-28): Robinhood-inspired P&L visualization
 *
 * Key improvements based on research:
 * - Large, color-coded P&L that "pops"
 * - Simplified columns (removed redundant Name, merged P&L columns)
 * - Visual ranking badges for best/worst performers
 * - Percentage change with color-coded badges
 * - Mini sparkline trend indicators (placeholder for future)
 * - Industry-standard green/red color coding
 */
const CurrentHoldings = ({ refreshTrigger = 0 }) => {
  const [selectedTab, setSelectedTab] = useState('all');
  const [holdingsData, setHoldingsData] = useState([]);
  const [loading, setLoading] = useState(true);
  // 🔧 2025-12-30 FIX: Prevent flickering on refresh - only show skeleton on INITIAL load
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('pnl'); // Sort by P&L by default
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchHoldingsData();
  }, [refreshTrigger]);

  const fetchHoldingsData = async () => {
    try {
      // 🔧 FIX: Only show loading skeleton on initial load, not on refreshes
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      // Fetch portfolio positions from ASTRA backend
      const positions = await astraAPI.getPortfolioAnalytics();

      if (positions && positions.items) {
        // Transform ASTRA data format to component format
        const transformedData = positions.items.map((holding, index) => {
          // 🔧 CRITICAL FIX (2025-10-31): P&L vs Unrealized P&L
          // For positions:
          // - Total P&L (P&L column) = Realized P&L (from closed trades for this symbol) + Unrealized P&L (from open position)
          // - Unrealized P&L column = Only unrealized from remaining open quantity
          // Backend now provides realized_pnl per symbol from closed trades
          const unrealizedPnl = holding.unrealized_pnl || 0;
          const realizedPnl = holding.realized_pnl || 0; // From closed trades for this symbol (backend provides this)
          const totalPnl = realizedPnl + unrealizedPnl; // Total P&L for this position = realized + unrealized
          
          return {
            id: holding.id || `${holding.symbol}-${index}`, // 🔧 FIX: Use unique ID to fix duplicate key warnings
            symbol: holding.symbol,
            name: holding.symbol, // Use symbol as name for now
            type: holding.symbol.includes('.NS') ? 'stock' : 'crypto', // Determine type based on symbol
            quantity: holding.quantity || 0,
            avgPrice: holding.entry_price || 0,
            currentPrice: holding.current_price || 0,
            pnl: totalPnl, // 🔧 FIX: Total P&L (realized + unrealized)
            pnlPercentage: holding.pnl_percentage || 0,
            unrealized_pnl: unrealizedPnl, // 🔧 FIX: Only unrealized P&L
            pnl_percentage: holding.pnl_percentage || 0,
            value: holding.current_value || 0
          };
        });

        setHoldingsData(transformedData);
      } else {
        throw new Error('Failed to fetch holdings data');
      }
    } catch (err) {
      console.error('Failed to fetch holdings data:', err);
      setError('Failed to load holdings data');
      // Fallback to demo data if API fails
      setHoldingsData([
        {
          symbol: 'RELIANCE',
          name: 'Reliance Industries Ltd',
          type: 'stock',
          quantity: 150,
          avgPrice: 2450.75,
          currentPrice: 2567.20,
          pnl: 17467.50,
          pnlPercentage: 4.75,
          value: 385080
        },
        {
          symbol: 'TCS',
          name: 'Tata Consultancy Services',
          type: 'stock',
          quantity: 200,
          avgPrice: 3245.60,
          currentPrice: 3398.45,
          pnl: 30570.00,
          pnlPercentage: 4.71,
          value: 679690
        }
      ]);
    } finally {
      setLoading(false);
      // 🔧 FIX: Mark initial load complete - subsequent refreshes won't show skeleton
      setIsInitialLoad(false);
    }
  };

  // Filter and sort holdings
  const filteredHoldings = holdingsData
    .filter(holding => {
      if (selectedTab === 'all') return true;
      return holding.type === selectedTab;
    })
    .sort((a, b) => {
      const aVal = a[sortBy] || 0;
      const bVal = b[sortBy] || 0;
      return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
    });

  // Find best and worst performers for visual badges
  const bestPerformer = holdingsData.reduce((best, h) =>
    (h.pnlPercentage > (best?.pnlPercentage || -Infinity)) ? h : best, null);
  const worstPerformer = holdingsData.reduce((worst, h) =>
    (h.pnlPercentage < (worst?.pnlPercentage || Infinity)) ? h : worst, null);

  // Calculate totals
  const totalPnL = holdingsData.reduce((sum, h) => sum + (h.pnl || 0), 0);
  const totalValue = holdingsData.reduce((sum, h) => sum + (h.value || 0), 0);

  const formatCurrency = (amount, compact = false) => {
    if (compact && Math.abs(amount) >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value?.toFixed(2)}%`;
  };

  // Toggle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // 🔧 2025-12-30 FIX: Only show skeleton on INITIAL load, not on refreshes
  // This prevents the entire box from flickering every 60 seconds
  if (loading && isInitialLoad) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-card border border-border rounded-lg p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Current Holdings</h2>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-warning rounded-full animate-live-pulse"></div>
            <span className="text-xs text-muted-foreground">Loading...</span>
          </div>
        </div>
        <SkeletonTable rows={5} columns={8} />
      </motion.div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Current Holdings</h2>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-error rounded-full"></div>
            <span className="text-xs text-muted-foreground">Error</span>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Icon name="AlertTriangle" size={24} className="text-error mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{error}</p>
            <button
              onClick={fetchHoldingsData}
              className="mt-2 px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-card border border-border rounded-lg p-6 glass-card-hover"
    >
      {/* Header with Total P&L Summary - Robinhood Style */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Current Holdings</h2>
          <p className="text-xs text-muted-foreground">{holdingsData.length} positions</p>
        </div>
        <div className="flex items-center space-x-4">
          {/* 🎯 HERO P&L DISPLAY - This makes it POP! */}
          <div className={`px-4 py-2 rounded-lg ${totalPnL >= 0 ? 'bg-success/20' : 'bg-error/20'}`}>
            <div className="text-xs text-muted-foreground">Total P&L</div>
            <div className={`text-xl font-bold ${totalPnL >= 0 ? 'text-success' : 'text-error'}`}>
              {totalPnL >= 0 ? '▲' : '▼'} {formatCurrency(Math.abs(totalPnL), true)}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full animate-live-pulse"></div>
            <span className="text-xs text-muted-foreground">Live</span>
          </div>
        </div>
      </div>

      {/* Tab Navigation + Sort Toggle */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex space-x-1">
          {['all', 'stock', 'crypto'].map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                selectedTab === tab
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {tab === 'all' ? '📊 All' : tab === 'stock' ? '🇮🇳 Stocks' : '₿ Crypto'}
              <span className="ml-1 opacity-75">({holdingsData.filter(h => tab === 'all' || h.type === tab).length})</span>
            </button>
          ))}
        </div>
        <div className="flex items-center space-x-2 text-xs">
          <span className="text-muted-foreground">Sort:</span>
          <button
            onClick={() => handleSort('pnl')}
            className={`px-2 py-1 rounded ${sortBy === 'pnl' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted'}`}
          >
            P&L {sortBy === 'pnl' && (sortOrder === 'desc' ? '↓' : '↑')}
          </button>
          <button
            onClick={() => handleSort('value')}
            className={`px-2 py-1 rounded ${sortBy === 'value' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted'}`}
          >
            Value {sortBy === 'value' && (sortOrder === 'desc' ? '↓' : '↑')}
          </button>
        </div>
      </div>

      {/* Holdings Cards - Robinhood-Style with Large P&L */}
      <div className="space-y-2">
        <AnimatePresence>
          {filteredHoldings?.map((holding, index) => {
            const isBest = bestPerformer?.symbol === holding.symbol && holding.pnl > 0;
            const isWorst = worstPerformer?.symbol === holding.symbol && holding.pnl < 0;

            return (
              <motion.div
                key={holding.id || holding.symbol}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                className={`
                  relative p-4 rounded-lg border transition-all cursor-pointer
                  hover:shadow-lg hover:scale-[1.01]
                  ${holding.pnl >= 0
                    ? 'border-success/30 hover:border-success/60 bg-gradient-to-r from-success/5 to-transparent'
                    : 'border-error/30 hover:border-error/60 bg-gradient-to-r from-error/5 to-transparent'
                  }
                `}
              >
                {/* Best/Worst Performer Badge */}
                {isBest && (
                  <div className="absolute -top-2 -right-2 bg-success text-white text-xs px-2 py-0.5 rounded-full font-medium shadow-lg">
                    🏆 Best
                  </div>
                )}
                {isWorst && (
                  <div className="absolute -top-2 -right-2 bg-error text-white text-xs px-2 py-0.5 rounded-full font-medium shadow-lg">
                    📉 Worst
                  </div>
                )}

                <div className="flex items-center justify-between">
                  {/* Left: Symbol & Details */}
                  <div className="flex items-center space-x-4">
                    {/* Symbol Badge */}
                    <div className={`
                      w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg
                      ${holding.type === 'crypto'
                        ? 'bg-gradient-to-br from-orange-500 to-amber-600 text-white'
                        : 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white'
                      }
                    `}>
                      {holding.symbol.slice(0, 2)}
                    </div>

                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-foreground">{holding.symbol}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${holding.type === 'crypto' ? 'bg-orange-500/20 text-orange-500' : 'bg-blue-500/20 text-blue-500'}`}>
                          {holding.type === 'crypto' ? 'CRYPTO' : 'NSE'}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {holding.quantity?.toLocaleString()} × ₹{holding.currentPrice?.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Middle: Price Change Trend (placeholder for sparkline) */}
                  <div className="hidden md:flex items-center space-x-6">
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">Avg</div>
                      <div className="font-medium">₹{holding.avgPrice?.toLocaleString()}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">Current</div>
                      <div className="font-medium">₹{holding.currentPrice?.toLocaleString()}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">Value</div>
                      <div className="font-medium">{formatCurrency(holding.value, true)}</div>
                    </div>
                  </div>

                  {/* Right: 🎯 P&L - THE MAIN ATTRACTION */}
                  <div className={`
                    text-right px-4 py-2 rounded-lg min-w-[140px]
                    ${holding.pnl >= 0
                      ? 'bg-success/20'
                      : 'bg-error/20'
                    }
                  `}>
                    <div className={`text-2xl font-bold ${holding.pnl >= 0 ? 'text-success' : 'text-error'}`}>
                      {holding.pnl >= 0 ? '▲' : '▼'} {formatCurrency(Math.abs(holding.pnl), true)}
                    </div>
                    <div className={`
                      inline-flex items-center px-2 py-0.5 rounded-full text-sm font-bold
                      ${holding.pnlPercentage >= 0
                        ? 'bg-success/30 text-success'
                        : 'bg-error/30 text-error'
                      }
                    `}>
                      {formatPercentage(holding.pnlPercentage)}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {filteredHoldings?.length === 0 && (
          <div className="text-center py-12 bg-muted/30 rounded-lg">
            <Icon name="Inbox" size={32} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground font-medium">No holdings found</p>
            <p className="text-xs text-muted-foreground mt-1">Start trading to see your positions here</p>
          </div>
        )}
      </div>

      {/* Footer Summary */}
      {holdingsData.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div>
              <span className="text-muted-foreground">Total Value:</span>
              <span className="ml-2 font-bold text-foreground">{formatCurrency(totalValue, true)}</span>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <Icon name="Clock" size={12} />
            <span>Auto-refreshing every 15s</span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default CurrentHoldings;